import { useState, type FormEvent, type ReactNode } from 'react'
import type { OrderDetailResponse, UpdateOrderRequest } from '@fitting-room/contracts'
import { ORDER_MEASUREMENT_KEYS, balanceDue, defaultRequiredDate, expectedPickup } from '@fitting-room/contracts/order-math'
import { Pencil } from 'lucide-react'
import { useAssignees, useSetOrderSalesStaff, useUpdateOrder } from '../../../lib/adminQueries'
import { assigneeName } from '../../../lib/appointments'
import { boutiqueDayOf, boutiqueTime } from '../../../lib/boutique'
import { shortDay } from '../../../lib/commission'
import { MEASUREMENT_LABEL, balanceDueSource, expectedPickupSource, pickupAhead, pickupComesEarly, pickupDateSource } from '../../../lib/orders'
import { useStaffToken } from '../../../lib/staffToken'
import { AssigneeSelect } from '../AssigneeSelect'
import { DatePicker } from '../DatePicker'
import { FormField } from '../FormSheet'
import { StaffMark } from '../StaffMark'
import { OrderNotesEditButton, type OrderNotesSection } from './OrderNotesPanel'
import { ReasonDialog } from './ReasonDialog'

/** Loose on purpose: the API has the real rule, this only catches the obvious typo. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A field of the invoice's header: a label over a value over a rule. */
export function InvoiceField({ label, children, wide }: { readonly label: string; readonly children: ReactNode; readonly wide?: boolean }) {
  return (
    <div className={`st-invoice-field ${wide ? 'sm:col-span-2' : ''}`}>
      <div className="st-eyebrow mb-1">{label}</div>
      <div className="break-words text-sm">{children}</div>
    </div>
  )
}

const none = <span className="st-meta">—</span>

/**
 * Who the order is for, as the top of the invoice reads: name, required
 * date, shipping address; email, phone, wedding date; then whose sale it is
 * and when the customer is expected to pick up — not on the invoice, but on
 * every order, paid in full or not. The customer's measurements, the other
 * request and the note are their own cards, edited from their own panel
 * (`OrderNotesPanel`), so only one place changes them.
 *
 * Edited in place: the fields turn into inputs, with the reason the history
 * asks for, as tperent asks it of every edit after an order is written. The
 * bridal stylist is changed on its own — it moves the commission, so it has
 * its own question.
 */
export function OrderHeaderCard({ detail }: { readonly detail: OrderDetailResponse }) {
  const [editing, setEditing] = useState(false)
  const { order } = detail
  const editable = order.status === 'open'

  if (editing) return <OrderHeaderForm detail={detail} onDone={() => setEditing(false)} />
  return (
    <section className="st-card px-4 py-3" aria-labelledby="order-customer">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h2 id="order-customer" className="st-eyebrow">
          Customer
        </h2>
        {editable ? (
          <button type="button" className="st-btn-quiet print-hide" onClick={() => setEditing(true)}>
            <Pencil size={13} aria-hidden />
            Edit
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-3">
        <InvoiceField label="Name">
          <span className="font-medium">{order.customerName}</span>
        </InvoiceField>
        <InvoiceField label="Required date">{order.requiredDate ? shortDay(order.requiredDate) : none}</InvoiceField>
        <InvoiceField label="Shipping address">
          {order.shippingAddress ? <span className="whitespace-pre-wrap">{order.shippingAddress}</span> : none}
        </InvoiceField>
        <InvoiceField label="Email">{order.customerEmail ? <span className="break-all">{order.customerEmail}</span> : none}</InvoiceField>
        <InvoiceField label="Phone">{order.customerPhone ? <span className="tabular-nums">{order.customerPhone}</span> : none}</InvoiceField>
        <InvoiceField label="Wedding date">{order.weddingDate ? shortDay(order.weddingDate) : none}</InvoiceField>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-3">
        <BridalStylist detail={detail} />
        {/* As the list row has it: the day ahead while something is, the day it all went home after. */}
        {pickupAhead({ status: order.status, lineCount: detail.lines.length, allPickedUp: detail.standing.allPickedUp }) ? (
          <>
            <InvoiceField label="Expected pickup">
              <span className="tabular-nums" data-testid="order-expected-pickup">
                {shortDay(detail.expectedPickup.date)}
              </span>
              <span className="st-meta block text-xs">{expectedPickupSource(detail.expectedPickup.source)}</span>
            </InvoiceField>
            <PickupDate detail={detail} />
          </>
        ) : detail.standing.allPickedUp && detail.standing.pickedUpAt ? (
          <InvoiceField label="Picked up">
            <span className="tabular-nums" data-testid="order-picked-up">
              {shortDay(boutiqueDayOf(detail.standing.pickedUpAt))}
            </span>
          </InvoiceField>
        ) : null}
      </div>
    </section>
  )
}

/**
 * The day the customer comes to collect, beside the day the gown is expected
 * in — never in its place (`orderPickupSchema`). Their pickup appointment's
 * day when they've booked one, else a day typed here, else nothing yet.
 */
function PickupDate({ detail }: { readonly detail: OrderDetailResponse }) {
  const { pickup } = detail
  return (
    <InvoiceField label="Pickup date">
      {pickup ? (
        <>
          <span className="tabular-nums" data-testid="order-pickup-date" data-source={pickup.source}>
            {shortDay(pickup.date)}
          </span>
          <span className="st-meta block text-xs">{pickupDateSource(pickup, pickup.at ? boutiqueTime(pickup.at) : undefined)}</span>
          {pickupComesEarly(pickup.date, detail.expectedPickup) ? (
            <span className="st-alert mt-1 block text-xs" role="status" data-testid="pickup-early">
              Before the expected pickup, {shortDay(detail.expectedPickup.date)}. Check the gown will be here in time.
            </span>
          ) : null}
        </>
      ) : (
        <span className="st-meta">Not booked yet</span>
      )}
    </InvoiceField>
  )
}

/**
 * The customer's measurements beside the other request, as the invoice
 * prints them. Its Edit opens the measurements-and-note panel at the
 * measurements.
 */
export function MeasurementsCard({
  detail,
  onEdit,
}: {
  readonly detail: OrderDetailResponse
  readonly onEdit: (section: OrderNotesSection) => void
}) {
  const { order } = detail
  return (
    <section className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]" data-testid="order-measurements">
      <div className="st-card overflow-hidden">
        <div className="st-invoice-band flex items-center justify-between gap-2">
          <span>Customer measurements</span>
          <OrderNotesEditButton detail={detail} section="measurements" onEdit={onEdit} />
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
          {ORDER_MEASUREMENT_KEYS.map((key) => (
            <div key={key} className="contents">
              <dt className="st-meta">{MEASUREMENT_LABEL[key]}</dt>
              <dd className="tabular-nums">{order.measurements?.[key] || '—'}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="st-card overflow-hidden">
        <div className="st-invoice-band">Other request</div>
        <p className="whitespace-pre-wrap break-words px-4 py-2.5 text-sm">{order.otherRequest || <span className="st-meta">None</span>}</p>
      </div>
    </section>
  )
}

/**
 * Whose sale it is — the "Bridal stylist", whose commission it is. Changing
 * it moves the commission, so it asks why: the select picks the person, and
 * the reason dialog makes it happen.
 */
function BridalStylist({ detail }: { readonly detail: OrderDetailResponse }) {
  const { token } = useStaffToken()
  const members = useAssignees(token).data?.members ?? []
  const set = useSetOrderSalesStaff(token, detail.order.id)
  const [pending, setPending] = useState<{ id: string | null } | null>(null)
  const current = detail.order.salesStaffId ?? null
  const editable = detail.order.status === 'open'
  const mark = members.find((m) => m.id === current)?.calendarMark

  return (
    <div className="flex flex-col gap-1">
      <div className="st-invoice-field">
        <div className="st-eyebrow mb-1">Bridal stylist</div>
        {editable ? (
          <AssigneeSelect
            members={members}
            value={current}
            label="Bridal stylist"
            onChange={(next) => (next === current ? undefined : setPending({ id: next }))}
          />
        ) : (
          <p className="flex items-center gap-1.5 text-sm">
            {current ? <StaffMark mark={mark} name={assigneeName(members, current)} /> : null}
            {assigneeName(members, current ?? undefined) ?? 'Nobody'}
          </p>
        )}
      </div>
      <p className="st-meta text-xs">The commission follows the bridal stylist.</p>
      <ReasonDialog
        open={pending !== null}
        title={`Make it ${pending?.id ? `${assigneeName(members, pending.id)}'s` : 'nobody’s'} sale?`}
        placeholder="Like: Mia did the fitting"
        confirmLabel="Change bridal stylist"
        pendingLabel="Saving…"
        pending={set.isPending}
        error={set.error?.message}
        onClose={() => {
          set.reset()
          setPending(null)
        }}
        onConfirm={(reason) => pending && set.mutate({ salesStaffId: pending.id, reason }, { onSuccess: () => setPending(null) })}
      >
        The commission on this order moves with it.
      </ReasonDialog>
    </div>
  )
}

/**
 * The header, the expected pickup and the balance due date, as inputs — who
 * the order is for and its dates. Only what changed is sent; the API words
 * the history from it and wants the reason. The measurements, the other
 * request and the note are not here: they have their own panel.
 */
export function OrderHeaderForm({ detail, onDone }: { readonly detail: OrderDetailResponse; readonly onDone: () => void }) {
  const { token } = useStaffToken()
  const { order } = detail
  const save = useUpdateOrder(token, order.id)
  const [name, setName] = useState(order.customerName)
  const [phone, setPhone] = useState(order.customerPhone ?? '')
  const [email, setEmail] = useState(order.customerEmail ?? '')
  const [weddingDate, setWeddingDate] = useState(order.weddingDate ?? '')
  const [requiredDate, setRequiredDate] = useState(order.requiredDate ?? '')
  const [address, setAddress] = useState(order.shippingAddress ?? '')
  // `null` is "back to the worked-out day"; `undefined` is "leave it".
  const [expectedPickupDate, setExpectedPickupDate] = useState<string | null | undefined>(undefined)
  // `null` is "back to following the expected pickup"; `undefined` is "leave it".
  const [balanceDueDate, setBalanceDueDate] = useState<string | null | undefined>(undefined)
  const [pickupDate, setPickupDate] = useState(order.pickupDate ?? '')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // The pickup as it will read once saved — what was typed; cleared, the day
  // the same rule as the API's works out from the lines' ETAs and holds;
  // else what the page was given — and the balance due date following it,
  // unless its own date is kept.
  const showsPickup = pickupAhead({ status: order.status, lineCount: detail.lines.length, allPickedUp: detail.standing.allPickedUp })
  const pickupSetByHand = expectedPickupDate === undefined ? detail.expectedPickup.source === 'set' : expectedPickupDate !== null
  const workedOut = expectedPickup({ orderDay: boutiqueDayOf(order.createdAt), lines: detail.lines })
  const shownPickup = expectedPickupDate === null ? workedOut.date : (expectedPickupDate ?? detail.expectedPickup.date)
  const owed = detail.balanceDue !== null
  const keepsOwnDue = balanceDueDate === undefined ? detail.balanceDue?.source === 'set' : balanceDueDate !== null
  const shownBalanceDue =
    balanceDueDate ??
    balanceDue({
      set: keepsOwnDue ? detail.balanceDue?.date : undefined,
      balanceMinor: detail.money.balanceMinor,
      pickup: { source: 'set', date: shownPickup },
    })?.date ??
    ''

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next: Record<string, string> = {}
    if (!name.trim()) next.name = 'The customer’s name, so the order can be found again.'
    if (email.trim() && !EMAIL.test(email.trim())) next.email = "That doesn't look like an email address."
    if (!reason.trim()) next.reason = 'Say why, for the order’s history.'
    setErrors(next)
    if (Object.keys(next).length > 0) return
    const body: UpdateOrderRequest = { reason: reason.trim() }
    const text = (now: string, was: string | undefined) => (now.trim() === (was ?? '') ? undefined : now.trim() || null)
    if (name.trim() !== order.customerName) body.customerName = name.trim()
    body.customerPhone = text(phone, order.customerPhone)
    body.customerEmail = text(email, order.customerEmail)
    body.shippingAddress = text(address, order.shippingAddress)
    if (weddingDate !== (order.weddingDate ?? '')) body.weddingDate = weddingDate || null
    if (requiredDate !== (order.requiredDate ?? '')) body.requiredDate = requiredDate || null
    if (expectedPickupDate !== undefined) body.expectedPickupDate = expectedPickupDate
    if (balanceDueDate !== undefined) body.balanceDueDate = balanceDueDate
    if (pickupDate !== (order.pickupDate ?? '')) body.pickupDate = pickupDate || null
    for (const key of Object.keys(body) as (keyof UpdateOrderRequest)[]) if (body[key] === undefined) delete body[key]
    save.mutate(body, { onSuccess: onDone })
  }

  return (
    <form className="st-card px-4 py-3" onSubmit={submit} noValidate aria-label="Edit the order">
      <h2 className="st-eyebrow mb-2.5">Customer</h2>
      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
        <FormField label="Name" error={errors.name}>
          <input className="st-input" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
        </FormField>
        <div className="mb-3 flex flex-col gap-1">
          <span className="st-eyebrow">Required date</span>
          <div className="flex items-center">
            <DatePicker value={requiredDate} onChange={setRequiredDate} label="Required date" placeholder="Not set" onClear={() => setRequiredDate('')} />
          </div>
          {!requiredDate && weddingDate ? (
            <button type="button" className="st-btn-quiet -ml-2 self-start text-xs" onClick={() => setRequiredDate(defaultRequiredDate(weddingDate) ?? '')}>
              Wedding date − 45 days
            </button>
          ) : null}
        </div>
        <FormField label="Shipping address">
          <textarea className="st-input" rows={2} value={address} onChange={(event) => setAddress(event.target.value)} />
        </FormField>
        <FormField label="Email" error={errors.email}>
          <input className="st-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
        </FormField>
        <FormField label="Phone">
          <input className="st-input" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="off" />
        </FormField>
        <div className="mb-3 flex flex-col gap-1">
          <span className="st-eyebrow">Wedding date</span>
          <div className="flex items-center">
            <DatePicker value={weddingDate} onChange={setWeddingDate} label="Wedding date" placeholder="Not set" onClear={() => setWeddingDate('')} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
        {showsPickup ? (
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Expected pickup</span>
            {/* Three dates abreast leave no room beside the picker: Clear goes under it. */}
            <div className="flex flex-wrap items-center gap-y-1">
              <DatePicker
                value={shownPickup}
                onChange={(day) => setExpectedPickupDate(day)}
                label="Expected pickup"
                onClear={pickupSetByHand ? () => setExpectedPickupDate(null) : undefined}
              />
            </div>
            <span className="st-meta text-xs">
              {expectedPickupDate === null
                ? `When you save: ${expectedPickupSource(workedOut.source)}`
                : expectedPickupDate
                  ? 'Set by hand'
                  : expectedPickupSource(detail.expectedPickup.source)}
            </span>
          </div>
        ) : null}
        {showsPickup ? (
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Pickup date</span>
            <div className="flex flex-wrap items-center gap-y-1">
              <DatePicker value={pickupDate} onChange={setPickupDate} label="Pickup date" placeholder="Not set" onClear={() => setPickupDate('')} />
            </div>
            <span className="st-meta text-xs">
              {detail.pickup?.source === 'appointment'
                ? `Their pickup appointment on ${shortDay(detail.pickup.date)} is shown while it’s booked.`
                : 'When they’ll come for it, if they haven’t booked a pickup.'}
            </span>
            {/* Against the day the header will show: the appointment's while it's booked, else this one. */}
            {pickupComesEarly(detail.pickup?.source === 'appointment' ? detail.pickup.date : pickupDate, {
              source: expectedPickupDate === null ? workedOut.source : pickupSetByHand ? 'set' : detail.expectedPickup.source,
              date: shownPickup,
            }) ? (
              <span className="st-alert text-xs" role="status">
                Before the expected pickup, {shortDay(shownPickup)}.
              </span>
            ) : null}
          </div>
        ) : null}
        {/* Nothing owed, no day for it to be due. */}
        {owed ? (
          <div className="mb-3 flex flex-col gap-1">
            <span className="st-eyebrow">Balance due date</span>
            <div className="flex flex-wrap items-center gap-2">
              <DatePicker value={shownBalanceDue} onChange={(day) => setBalanceDueDate(day)} label="Balance due date" />
              {keepsOwnDue ? (
                <button type="button" className="st-btn-quiet text-xs" onClick={() => setBalanceDueDate(null)}>
                  Follow the pickup
                </button>
              ) : null}
            </div>
            <span className="st-meta text-xs">
              {balanceDueDate === null ? 'Back to the expected pickup day when you save.' : balanceDueSource(keepsOwnDue ? 'set' : 'pickup')}
            </span>
          </div>
        ) : null}
      </div>

      <FormField label="Reason for the change" error={errors.reason} hint="It stays on the order’s history.">
        <input className="st-input" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Like: spelling of their name" />
      </FormField>

      {save.isError ? (
        <p className="st-alert mb-3" role="alert">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button type="button" className="st-btn-ghost" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="st-btn" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}
